import { useMemo } from 'react';
import { balanceSheet, buildCashflow, computeMonthlyObligations, daysOverdue, monthlyTotals, typicalMonthlyExpense, type CashEvent } from '../../../finance';
import { useData } from '../../store/data';
import { toDate } from '../transactions/entries';
import { paymentCap, cappedPayment } from '../planned/paymentCap';

const keyOf = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

export interface CashflowData {
  sources: any[]; expenses: any[]; plannedExpenses: any[]; plannedIncomes: any[]; plannedPayments: any[]; recurringTemplates: any[]; debts: any[]; creditPayments: any[];
}

/** Mô hình dòng tiền N ngày tới (dùng chung cho tab Dòng tiền và Tổng quan). Chỉ gồm khoản đã biết + ước tính chi hằng ngày riêng. */
export const cashflowModel = (data: CashflowData, horizon: number, now: Date = new Date()) => {
  const { sources, expenses, plannedExpenses, plannedIncomes, plannedPayments, recurringTemplates, debts, creditPayments } = data;

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Cùng định nghĩa "thanh khoản" với Tổng quan / Tài sản & Nợ (ví không phải thẻ, không phải tài sản dài hạn)
    const liquid = balanceSheet(sources, []).liquid;
    const events: CashEvent[] = [];
    // Chi bằng THẺ TÍN DỤNG chưa làm tiền ra khỏi ví: tiền chỉ ra khi trả thẻ (đã có ở khoản trả thẻ / nghĩa vụ tối thiểu)
    const cardIds = new Set(sources.filter(s => s.type === 'credit').map(s => s.id));

    // 1. Khoản dự kiến đang chờ (đã quá hạn thì dồn về hôm nay)
    for (const p of plannedExpenses) if (p.status === 'pending' && !cardIds.has(p.walletId)) { const d = toDate(p.plannedDate); if (d) events.push({ date: d, amount: -(Number(p.amount) || 0), label: [p.category, p.note].filter(Boolean).join(' · ') || 'Dự chi', kind: 'expense' }); }
    // Dự thu đã quá hạn mà chưa xác nhận: chưa chắc đã nhận → KHÔNG cộng vào dự báo (chi quá hạn thì vẫn tính — thận trọng)
    let skippedIncome = 0, skippedIncomeCount = 0;
    for (const p of plannedIncomes) if (p.status === 'pending') {
      const d = toDate(p.plannedDate); if (!d) continue;
      if (daysOverdue(d, today) > 0) { skippedIncome += Number(p.amount) || 0; skippedIncomeCount++; continue; }
      events.push({ date: d, amount: Number(p.amount) || 0, label: [p.category, p.description].filter(Boolean).join(' · ') || 'Dự thu', kind: 'income' });
    }
    // Khoản trả thẻ/nợ không thể vượt số còn nợ thực tế (mẫu cũ có thể lớn hơn dư nợ hiện tại)
    for (const p of plannedPayments) if (p.status === 'pending') { const d = toDate(p.plannedDate); if (d) events.push({ date: d, amount: -cappedPayment(Number(p.amount) || 0, paymentCap(p, sources, debts)), label: p.paymentType === 'debt' ? `Trả nợ ${p.debtName ?? ''}` : `Trả thẻ ${p.creditCardName ?? ''}`, kind: 'payment' }); }

    // 2. Nghĩa vụ thẻ/nợ tháng này chưa có kế hoạch (ước tính tối thiểu) — khoản có kế hoạch đã nằm ở mục 1
    const ob = computeMonthlyObligations(debts, sources, plannedPayments, creditPayments, now, recurringTemplates);
    for (const it of ob.items) if (it.source !== 'plan') events.push({ date: it.dueDate && it.dueDate >= today ? it.dueDate : it.dueDate ? today : new Date(now.getFullYear(), now.getMonth() + 1, 0), amount: -it.amount, label: `${it.label} (ước tính tối thiểu)`, kind: 'payment' });

    // 3. Khoản định kỳ của các tháng tới chưa được sinh ra
    const existing = new Set<string>([...plannedExpenses, ...plannedIncomes, ...plannedPayments].map((p: any) => p.id));
    const monthsAhead = Math.ceil(horizon / 28) + 1;
    for (const t of recurringTemplates) {
      if (!t.active || !(Number(t.amount) > 0)) continue;
      if (t.type === 'expense' && cardIds.has(t.walletId)) continue;
      for (let i = 0; i <= monthsAhead; i++) {
        const first = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const y = first.getFullYear(), m = first.getMonth() + 1, key = keyOf(y, m);
        if (t.startMonth && key < t.startMonth) continue;
        if (t.frequency === 'yearly' && (Number(t.monthOfYear) || 1) !== m) continue;
        if (existing.has(`${t.id}_${key}`)) continue;
        const day = Math.min(Math.max(1, Number(t.dayOfMonth) || 1), new Date(y, m, 0).getDate());
        events.push({ date: new Date(y, m - 1, day), amount: (t.type === 'income' ? 1 : -1) * (t.type === 'payment' ? cappedPayment(Number(t.amount), paymentCap(t, sources, debts)) : Number(t.amount)), label: t.name, kind: t.type === 'income' ? 'income' : t.type === 'payment' ? 'payment' : 'expense' });
      }
    }

    const known = buildCashflow({ liquidNow: liquid, today, horizonDays: horizon, events });

    // Chi tiêu hằng ngày KHÔNG nằm trong các khoản dự: trung vị chi bằng ví thanh khoản (chi bằng thẻ sẽ hiện ở khoản trả thẻ) trừ phần định kỳ đã biết
    const isCard = new Set(sources.filter(s => s.type === 'credit').map(s => s.id));
    const cashExpenses = expenses.filter((e: any) => !isCard.has(e.walletId));
    const typical = typicalMonthlyExpense(monthlyTotals([], cashExpenses, now));
    const rec = plannedExpenses.filter((p: any) => p.isRecurring && p.status !== 'cancelled' && p.year && p.month);
    const lastYm = rec.length ? Math.max(...rec.map((p: any) => p.year * 12 + p.month)) : 0;
    const recurringMonthly = rec.filter((p: any) => p.year * 12 + p.month === lastYm).reduce((s: number, p: any) => s + (Number(p.amount) || 0), 0);
    const variableMonthly = Math.max(0, typical - recurringMonthly);
    const perDay = variableMonthly / 30;
    const withVariable = known.days.map((d, i) => d.balance - perDay * i);
    const lowVar = Math.min(...withVariable);
    const lowVarIdx = withVariable.indexOf(lowVar);
    const firstNegVarIdx = withVariable.findIndex(b => b < 0);

    return { skippedIncome, skippedIncomeCount, known, perDay, variableMonthly, withVariable, lowVar, lowVarDate: known.days[lowVarIdx].date, firstNegVar: firstNegVarIdx >= 0 ? known.days[firstNegVarIdx].date : null, today };
};

export const useCashflowModel = (horizon: 30 | 60) => {
  const { sources, expenses, plannedExpenses, plannedIncomes, plannedPayments, recurringTemplates, debts, creditPayments } = useData(s => s.data);
  return useMemo(() => cashflowModel({ sources, expenses, plannedExpenses, plannedIncomes, plannedPayments, recurringTemplates, debts, creditPayments }, horizon),
    [sources, expenses, plannedExpenses, plannedIncomes, plannedPayments, recurringTemplates, debts, creditPayments, horizon]);
};
